import { useState } from "react";

import {
  useSendDmToTelegram,
  useSendToTelegram,
} from "@/hooks/useTelegram";

interface Props {
  postId: number;
  /** Conversion preset (e.g. "gif", "mp4_compact"). Pass `null` to send
   *  the post's *original* source file via the media proxy — used on the
   *  post-detail view for posts that don't need a conversion. */
  preset: string | null;
  /** Channel URL for the success-toast hyperlink (from /telegram/status). */
  channelUrl: string | null;
  /** "compact" → narrow blue pill sized to sit next to "Download" /
   *  "Share link" in the DownloadPanel job rows.
   *  "full" → wider blue pill matching the surrounding row spacing in the
   *  PostDetail Links section. */
  variant?: "compact" | "full";
  /** Where the bot posts the file. "channel" (default) hits the shared
   *  channel; "dm" hits the calling user's linked Telegram DM (requires
   *  the link flow in Settings — server-side returns 409 if not linked). */
  target?: "channel" | "dm";
}

/** Push a file into the shared Telegram channel.
 *
 * Click is best-effort and idempotent on the bot side — Telegram just shows
 * the file twice if the user double-clicks. On success: button flashes
 * "✓ Sent → channel ↗" (a hyperlink to the channel) for 1.5s. On failure:
 * shows "✗ Send failed" and surfaces the message in the title tooltip. */
export function TelegramSendButton({
  postId,
  preset,
  channelUrl,
  variant = "compact",
  target = "channel",
}: Props) {
  const channelMutation = useSendToTelegram();
  const dmMutation = useSendDmToTelegram();
  const { mutate, isPending } =
    target === "dm" ? dmMutation : channelMutation;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onClick() {
    setError(null);
    mutate(
      { post_id: postId, preset },
      {
        onSuccess: () => {
          setSent(true);
          window.setTimeout(() => setSent(false), 1500);
        },
        onError: (e) => setError(e.message),
      },
    );
  }

  const isCompact = variant === "compact";
  const sizing = isCompact
    ? "text-xs px-2 py-1 min-h-[32px]"
    : "text-sm px-3 py-2 min-h-[40px]";
  const idleClass = `inline-flex items-center gap-1 ${sizing} rounded transition-colors bg-blue-700 hover:bg-blue-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed`;
  const errorClass = `inline-flex items-center gap-1 ${sizing} rounded transition-colors bg-red-800 hover:bg-red-700 text-white font-semibold`;
  const sentClass = `inline-flex items-center gap-1 ${sizing} rounded bg-green-800 text-white font-semibold`;

  if (sent) {
    if (target === "dm") {
      return <span className={sentClass}>✓ Sent to your DM</span>;
    }
    if (channelUrl) {
      return (
        <a
          href={channelUrl}
          target="_blank"
          rel="noreferrer noopener"
          className={sentClass}
        >
          ✓ Sent → channel ↗
        </a>
      );
    }
    return <span className={sentClass}>✓ Sent to channel</span>;
  }

  const isDm = target === "dm";
  const label = isDm
    ? variant === "compact" ? "DM" : "Send to my DM"
    : preset === null ? "Send to Telegram" : "Telegram";
  const tooltip =
    error ??
    (isDm
      ? "Send this privately to your Telegram DMs (only you see it)"
      : preset === null
        ? "Send this post's original animation to the shared Telegram channel"
        : "Send this conversion to the shared Telegram channel");

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isPending}
      title={tooltip}
      className={error ? errorClass : idleClass}
    >
      ✈ {isPending ? "Sending…" : error ? "Send failed" : label}
    </button>
  );
}
